// static/table_engine_src/format/window_exports.js
import { clampInt } from "../util/number.js";
import { colScale, normalizeFormat } from "./format_core.js";
import { formatNumber, parseNumber } from "./format_numbers.js";
import { clearRawDatasets, getAnyRawDataset } from "./format_raw.js";

/**
 * Installs global/window helpers.
 *
 * Goals:
 *  - Prefer the *engine's* formatting functions when available (prevents drift).
 *  - Keep robust fallbacks for legacy code paths / non-engine contexts.
 *
 * Usage:
 *   import { installWindowExports } from "./window_exports.js";
 *   installWindowExports(ctx);
 */
export function installWindowExports(ctx = null) {
  if (typeof window === "undefined") return;

  // Expose parser for non-TE consumers (e.g. page controllers) to avoid drift.
  window.teParseNumber = parseNumber;

  // ---------------------------------------------------------
  // window.effectiveFormatForEl
  // Prefer engine-installed function (ctx), else install fallback once.
  // ---------------------------------------------------------
  if (ctx?.effectiveFormatForEl && typeof ctx.effectiveFormatForEl === "function") {
    window.effectiveFormatForEl = ctx.effectiveFormatForEl;
  } else if (typeof window.effectiveFormatForEl !== "function") {
    window.effectiveFormatForEl = function effectiveFormatForEl(el, col, fallbackDp = 4) {
      const base =
        col && typeof col === "object"
          ? normalizeFormat(col)
          : { dp: fallbackDp, int: false, strip: false };

      const fmt = { ...base };

      if (!el || !el.dataset) return fmt;

      // Only treat dp/int/strip/group as explicit per-cell when:
      //  - teCellFmt=1 (explicit override), OR
      //  - teAutoFormat is NOT "1" (caller-provided hints)
      const explicit = (el.dataset.teCellFmt === "1") || (el.dataset.teAutoFormat !== "1");
      if (!explicit) return fmt;

      if (el.dataset.int != null && el.dataset.int !== "") {
        const s = String(el.dataset.int).toLowerCase();
        fmt.int = (s === "1" || s === "true");
      }
      if (el.dataset.dp != null && el.dataset.dp !== "") {
        const dp = Number(el.dataset.dp);
        if (Number.isFinite(dp)) fmt.dp = clampInt(dp, 0, 12);
      }
      if (el.dataset.strip != null && el.dataset.strip !== "") {
        const s = String(el.dataset.strip).toLowerCase();
        fmt.strip = (s === "1" || s === "true");
      }
      if (el.dataset.group != null && el.dataset.group !== "") {
        const s = String(el.dataset.group).toLowerCase();
        fmt.group = (s === "1" || s === "true");
      } else if (el.dataset.thousands != null && el.dataset.thousands !== "") {
        const s = String(el.dataset.thousands).toLowerCase();
        fmt.group = (s === "1" || s === "true");
      }

      if (el.dataset.locale != null && el.dataset.locale !== "") {
        fmt.locale = String(el.dataset.locale);
      }
      if (el.dataset.decimalSep != null && el.dataset.decimalSep !== "") {
        fmt.decimalSep = String(el.dataset.decimalSep);
      } else if (el.dataset.decimal != null && el.dataset.decimal !== "") {
        fmt.decimalSep = String(el.dataset.decimal);
      }
      if (el.dataset.groupSep != null && el.dataset.groupSep !== "") {
        fmt.groupSep = String(el.dataset.groupSep);
      } else if (el.dataset.thousandSep != null && el.dataset.thousandSep !== "") {
        fmt.groupSep = String(el.dataset.thousandSep);
      }

      return fmt;
    };
  }

  // ---------------------------------------------------------
  // window.teFormatNumberInput
  // - For TE numeric inputs, reformat deterministically.
  // - Prefer engine.reformatNumericInput when present.
  // - Otherwise fallback (legacy) implementation.
  // ---------------------------------------------------------
  if (typeof window.teFormatNumberInput !== "function") {
    window.teFormatNumberInput = function teFormatNumberInput(el, colOrNull = null, fallbackDp = 4) {
      if (!el || !el.dataset) return;

      // Guard: only run on likely TE cells to avoid touching random app inputs
      const inTeContainer =
        !!el.closest?.(".table-container table") ||
        !!el.closest?.("table.te-auto, table.te-fill") ||
        !!el.closest?.("table");

      const isTeCell =
        el.hasAttribute("data-field") ||
        el.hasAttribute("data-col-key") ||
        !!el.closest?.("td[data-col-key], td[data-field]");

      if (!(inTeContainer && isTeCell)) return;

      const table = el.closest?.("table") || null;
      const eng = table && table.__te_engine;

      // Resolve column key (if needed)
      const key =
        el.dataset.field ||
        el.dataset.colKey ||
        el.closest?.("td[data-col-key]")?.dataset?.colKey ||
        el.closest?.("td[data-field]")?.dataset?.field ||
        null;

      // 1) Prefer engine formatting logic if available
      if (eng && typeof eng.reformatNumericInput === "function") {
        let col = colOrNull;
        if (!col || typeof col !== "object") {
          // Prefer Map if exposed, else search spec.columns
          col =
            (key && eng.colByKey?.get?.(key)) ||
            (key && eng.spec?.columns?.find?.((c) => c && c.key === key)) ||
            null;
        }

        if (col) {
          eng.reformatNumericInput(el, col);
          return;
        }
        // If we can't resolve the column, fall through to fallback.
      }

      // 2) Fallback implementation (engine-less / legacy)
      let col = colOrNull;
      if ((!col || typeof col !== "object") && key) {
        // Try to discover column from engine if it exists but didn't expose reformatNumericInput
        const colFromEng =
          (eng && (eng.colByKey?.get?.(key) || eng.spec?.columns?.find?.((c) => c && c.key === key))) || null;
        if (colFromEng) col = colFromEng;
      }

      const sc = col ? colScale(col) : 1;

      // Prefer canonical raw (unscaled)
      const raw0 = getAnyRawDataset(el).trim();
      const s = raw0 !== "" ? raw0 : String(el.value ?? "").trim();

      if (s === "") {
        el.value = "";
        clearRawDatasets(el);
        return;
      }

      // Use effectiveFormatForEl if present (respects teAutoFormat / teCellFmt)
      const fmt0 =
        typeof window.effectiveFormatForEl === "function"
          ? window.effectiveFormatForEl(el, col || null, fallbackDp)
          : { dp: fallbackDp, int: false, strip: false };

      const n = raw0 !== "" ? parseNumber(s, NaN) : parseNumber(s, NaN, fmt0);
      if (!Number.isFinite(n)) return;

      // Native number inputs reject grouped values like "10,000.00".
      const isNumInput = el.tagName === "INPUT" && String(el.type || "").toLowerCase() === "number";
      const fmt = isNumInput
        ? { ...fmt0, group: false, thousands: false, locale: "", decimalSep: ".", groupSep: "," }
        : fmt0;

      const nCanon = (raw0 !== "") ? n : (n / sc);
      const canon = fmt.int ? Math.round(nCanon) : nCanon;

      el.dataset.raw = String(canon);
      el.value = formatNumber(canon * sc, fmt);
    };
  }
}
